// Analytics utility functions for tracking user interactions

import { AFFILIATE_CLICK_SEND_TO, loadGoogleAds } from "@/lib/consent";
import { trackInternalEvent } from "@/lib/internal-analytics";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Track affiliate link clicks to measure conversions
 * This helps reduce bounce rate by recording interactions before users leave
 */
export const trackAffiliateClick = (buttonId: string, ref: string) => {
  void trackInternalEvent("affiliate_click");
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'affiliate_click', {
      event_category: 'engagement',
      event_label: buttonId,
      ref_source: ref,
      non_interaction: false, // This marks it as an interaction, reducing bounce rate
    });
  }

  // Report the outbound store click to Google Ads as a conversion
  if (typeof window !== 'undefined') {
    void loadGoogleAds().then(() => {
      window.gtag?.('event', 'conversion', {
        send_to: AFFILIATE_CLICK_SEND_TO,
        value: 1.0,
        currency: 'USD',
      });
    });
  }
};


/**
 * Track newsletter signup with source attribution
 */
export const trackNewsletterSignup = (source: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'newsletter_signup', {
      event_category: 'engagement',
      event_label: source,
      non_interaction: false,
    });

    window.gtag('event', 'generate_lead', {
      event_category: 'lead_generation',
      event_label: source,
    });

    console.log('Tracked newsletter signup:', source);
  }
};

/**
 * Track lead magnet download/request
 */
export const trackLeadMagnetDownload = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'lead_magnet_download', {
      event_category: 'engagement',
      event_label: 'crystal_hydration_guide',
      non_interaction: false,
    });

    console.log('Tracked lead magnet download');
  }
};

/**
 * Track scroll depth milestones (25%, 50%, 75%, 100%)
 */
let scrollMilestones = new Set<number>();

export const initScrollTracking = () => {
  if (typeof window === 'undefined') return;

  // Reset on page load
  scrollMilestones = new Set<number>();

  let rafId: number | null = null;

  const handleScroll = () => {
    if (rafId) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = Math.round((scrollTop / docHeight) * 100);

      const milestones = [25, 50, 75, 90, 100];
      
      for (const milestone of milestones) {
        if (scrollPercent >= milestone && !scrollMilestones.has(milestone)) {
          scrollMilestones.add(milestone);
          trackScrollDepth(milestone);
        }
      }
    });
  };

  window.addEventListener('scroll', handleScroll, { passive: true });
  
  return () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

export const trackScrollDepth = (percentage: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'scroll_depth', {
      event_category: 'engagement',
      event_label: `${percentage}%`,
      value: percentage,
      non_interaction: true, // Scroll is passive, don't affect bounce rate
    });

    console.log('Tracked scroll depth:', percentage + '%');
  }
};

/**
 * Track time on page
 */
let pageStartTime: number | null = null;
let timeTrackingInterval: ReturnType<typeof setInterval> | null = null;

export const initTimeTracking = () => {
  if (typeof window === 'undefined') return;

  pageStartTime = Date.now();
  
  // Track time milestones: 30s, 60s, 120s, 300s
  const milestones = [30, 60, 120, 300];
  let currentMilestoneIndex = 0;

  timeTrackingInterval = setInterval(() => {
    if (!pageStartTime) return;
    
    const timeOnPage = Math.floor((Date.now() - pageStartTime) / 1000);
    
    if (currentMilestoneIndex < milestones.length && timeOnPage >= milestones[currentMilestoneIndex]) {
      trackTimeOnPage(milestones[currentMilestoneIndex]);
      currentMilestoneIndex++;
    }

    // Stop tracking after 5 minutes
    if (currentMilestoneIndex >= milestones.length && timeTrackingInterval) {
      clearInterval(timeTrackingInterval);
    }
  }, 5000); // Check every 5 seconds

  return () => {
    if (timeTrackingInterval) {
      clearInterval(timeTrackingInterval);
    }
  };
};

export const trackTimeOnPage = (seconds: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'time_on_page', {
      event_category: 'engagement',
      event_label: `${seconds}s`,
      value: seconds,
      non_interaction: true,
    });

    console.log('Tracked time on page:', seconds + 's');
  }
};

/**
 * Track CTA interactions
 */
export const trackCTAClick = (ctaName: string, location: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'cta_click', {
      event_category: 'engagement',
      event_label: ctaName,
      cta_location: location,
      non_interaction: false,
    });

    console.log('Tracked CTA click:', ctaName, location);
  }
};

/**
 * Track popup interactions
 */
export const trackPopupShown = (popupName: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'popup_shown', {
      event_category: 'engagement',
      event_label: popupName,
      non_interaction: true,
    });

    console.log('Tracked popup shown:', popupName);
  }
};

export const trackPopupClosed = (popupName: string, converted: boolean) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'popup_closed', {
      event_category: 'engagement',
      event_label: popupName,
      converted: converted,
      non_interaction: true,
    });

    console.log('Tracked popup closed:', popupName, 'converted:', converted);
  }
};

/**
 * Track AI / LLM referrer traffic (ChatGPT, Perplexity, Claude, Gemini, Copilot, etc.).
 * Fires once per session on first page load. Helps measure GEO/AIO performance.
 */
const AI_REFERRERS: Array<{ pattern: RegExp; source: string }> = [
  { pattern: /chat\.openai\.com|chatgpt\.com|openai\.com/i, source: 'chatgpt' },
  { pattern: /perplexity\.ai/i, source: 'perplexity' },
  { pattern: /claude\.ai|anthropic\.com/i, source: 'claude' },
  { pattern: /gemini\.google\.com|bard\.google\.com/i, source: 'gemini' },
  { pattern: /copilot\.microsoft\.com|bing\.com\/chat/i, source: 'copilot' },
  { pattern: /you\.com/i, source: 'you' },
  { pattern: /poe\.com/i, source: 'poe' },
  { pattern: /duckduckgo\.com\/\?.*ia=chat/i, source: 'duckassist' },
];

export const trackAIReferrer = () => {
  if (typeof window === 'undefined' || !document.referrer) return;
  try {
    const sessionFlag = 'ai_referrer_tracked';
    if (sessionStorage.getItem(sessionFlag)) return;

    const ref = document.referrer;
    const match = AI_REFERRERS.find((r) => r.pattern.test(ref));
    if (!match) return;

    sessionStorage.setItem(sessionFlag, '1');

    if (window.gtag) {
      window.gtag('event', 'ai_referral', {
        event_category: 'acquisition',
        event_label: match.source,
        ai_source: match.source,
        referrer: ref,
        landing_page: window.location.pathname,
        non_interaction: true,
      });
      window.gtag('event', 'conversion', {
        event_category: 'ai_traffic',
        event_label: match.source,
      });
    }
    console.log('Tracked AI referrer:', match.source);
  } catch {
    // sessionStorage may be blocked; ignore
  }
};
